<template>
  <div class="bg-white py-2">
    <button class="w-full" @click.stop="openModal">
      <img
        :src="optimizedImageUrl"
        class="w-full aspect-[4/3] object-cover"
        :alt="content.title"
      />
    </button>

    <Modal
      v-model:open="isModalOpen"
      :size="ModalSize.XL"
      :height="ModalHeight.LG"
      @close="closeModal"
    >
      <template #header>
        <div
          v-if="!content.isCustomizable"
          class="flex justify-between items-center bg-white"
        >
          <button
            class="ml-auto pt-3 px-5 text-on-surface-variant hover:text-on-surface transition-colors"
            @click="closeModal"
          >
            <Icon :icon="XMarkSvg" class="w-6 h-6" />
          </button>
        </div>
      </template>
      <template #body>
        <div class="flex flex-col md:flex-row gap-10 p-6 h-full bg-white">
          <div class="w-full md:flex-1 md:max-w-2xl">
            <div
              v-if="content.isCustomizable && isPdfFile"
              class="bg-surface rounded-lg items-center justify-center object-contain h-full flex"
            >
              <FilePrintPreview
                ref="filePrintPreviewRef"
                :file-url="pdfFileUrl"
                :content-id="content.id"
                :file-id="content.id"
                :is-customizable="content.isCustomizable"
                :quadrants="content.quadrants"
                :selected-logo-id="selectedLogoId"
                @update:customization="handleCustomizationUpdate"
                @personalization-changed="handlePersonalizationChanged"
                @customization-ready="handleCustomizationReady"
              />
              <div
                v-if="isGeneratingPdf"
                class="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center z-50"
              >
                <div class="text-center p-6">
                  <div
                    class="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"
                  ></div>
                  <h3 class="text-lg font-semibold text-on-surface mb-2">
                    {{ $t('print.generating-pdf') }}
                  </h3>
                  <p class="text-sm text-on-surface-variant max-w-xs">
                    We're creating your personalized PDF with your logo. This
                    usually takes 10-15 seconds.
                  </p>
                </div>
              </div>
            </div>

            <div
              v-else-if="content.isCustomizable && !isPdfFile"
              class="bg-surface rounded-lg p-4"
            >
              <div class="relative">
                <img
                  :src="optimizedImageUrl"
                  class="w-full h-full object-contain rounded-lg"
                  :alt="content.title"
                />
                <div class="absolute bottom-2 left-2">
                  <Chip
                    :size="ChipSize.SM"
                    :variant="ChipVariant.FILLED"
                    :icon="SparkleIcon"
                    :color="ChipColor.PRIMARY"
                  >
                    {{ $t('content.customizable') }}
                  </Chip>
                </div>
              </div>
              <div class="text-center py-8">
                <Icon
                  :icon="SparkleIcon"
                  class="w-8 h-8 text-on-surface-variant mx-auto mb-2"
                />
                <p class="text-sm text-on-surface-variant">
                  {{ $t('print.personalization-not-supported') }}
                </p>
              </div>
            </div>

            <div v-else class="bg-surface rounded-lg overflow-hidden h-full">
              <img
                :src="optimizedImageUrl"
                class="w-full h-full object-contain"
                :alt="content.title"
              />
            </div>
          </div>

          <div class="w-full md:w-80 flex flex-col gap-2">
            <div>
              <div
                v-if="content.isCustomizable"
                class="flex justify-between items-center bg-white py-2"
              >
                <Chip
                  :size="ChipSize.SM"
                  :variant="ChipVariant.FILLED"
                  :icon="SparkleIcon"
                  :color="ChipColor.SECONDARY"
                  class="!bg-surface !rounded-sm"
                  style="color: #000"
                >
                  {{ $t('content.personalizable') }}
                </Chip>

                <button
                  class="ml-auto text-on-surface-variant hover:text-on-surface transition-colors"
                  @click="closeModal"
                >
                  <Icon :icon="XMarkSvg" class="w-6 h-6" />
                </button>
              </div>
              <h2 class="text-2xl font-semibold text-on-surface mb-1">
                {{ content.title }}
              </h2>
              <div class="text-sm">
                <div class="flex justify-between">
                  <span class="text-on-surface-variant"
                    >{{ $t('print.medium') }}:</span
                  >
                  <span class="font-medium">{{
                    content.mediumDescription
                  }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-on-surface-variant"
                    >{{ $t('print.dimensions') }}:</span
                  >
                  <span class="font-medium">{{ content.dimensions }}</span>
                </div>
                <template v-if="content.printProduct?.sku">
                  <div class="flex justify-between">
                    <span class="text-on-surface-variant">SKU:</span>
                    <span class="font-medium">{{
                      content.printProduct.sku
                    }}</span>
                  </div>
                </template>
              </div>
            </div>

            <div v-if="content.isCustomizable">
              <div class="my-5">
                <div class="flex items-center">
                  <h4 class="text-lg font-semibold text-on-surface">
                    Personalize
                  </h4>
                  <Tooltip
                    text="We take 2–4 business days to carefully review and process your order before it ships. Shipping times may vary. All orders are final once placed."
                  >
                    <template #default="{ attrs, openTooltip, closeTooltip }">
                      <icon
                        v-bind="attrs"
                        class="ml-2 w-4 h-4 text-on-surface-variant"
                        :icon="TooltipSvg"
                        @mouseenter="openTooltip"
                        @mouseleave="closeTooltip"
                      />
                    </template>
                  </Tooltip>
                </div>

                <p class="text-sm text-on-surface-variant mb-2">
                  Upload your practice logo, resize, move within the available
                  areas and we'll handle all the rest!
                </p>
                <SelectLogoModal v-model="selectedLogoId" />
              </div>
            </div>

            <div class="mb-3">
              <h3 class="text-lg font-semibold text-on-surface mb-2">
                {{ $t('print.quantity') }}
              </h3>
              <div
                v-if="isOutOfStock"
                class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4"
              >
                <div class="flex items-center gap-2">
                  <span class="text-red-700 font-medium">Out of Stock</span>
                </div>
                <p class="text-red-600 text-sm mt-1">
                  This item is currently unavailable due to inventory limits.
                  <span v-if="inventoryInfo.hasInventoryLimit">
                    Available: {{ inventoryInfo.availableQuantity }} /
                    {{ inventoryInfo.totalAllocated }}
                  </span>
                </p>
              </div>
              <SelectInput
                v-model="selectedQuantity"
                :options="quantityOptions"
                placeholder="Select Quantity"
                option-value-name="quantity"
                option-label-name="displayText"
                class="w-1/2"
                :rules="[isRequired]"
                :disabled="isOutOfStock"
              >
                <template #option="{ option }">
                  <div class="w-full">
                    <span class="font-medium">{{ option.displayText }}</span>
                  </div>
                </template>
              </SelectInput>
            </div>

            <Btn
              :color="BtnColor.PRIMARY"
              :variant="BtnVariant.FILLED"
              :size="BtnSize.LG"
              :disabled="!selectedQuantity || isGeneratingPdf || isOutOfStock"
              :loading="isGeneratingPdf"
              @click="handleAddToCart"
            >
              <template v-if="isGeneratingPdf">
                {{ $t('print.generating-pdf') }}
              </template>
              <template v-else-if="isOutOfStock"> Out of Stock </template>
              <template v-else>
                {{ $t('print.add-to-cart') }}
              </template>
            </Btn>

            <div class="pt-10 flex gap-2 flex-col">
              <h1 class="text-lg font-semibold text-on-surface mb-1">
                {{ $t('print.detail') }}
              </h1>
              <span class="text-md text-on-surface-variant">{{
                content.description
              }}</span>
            </div>
          </div>
        </div>
      </template>

      <template #actions>
        <!-- left actions empty on purpose -->
      </template>
    </Modal>

    <Modal
      v-model:open="showPersonalizationWarning"
      as-form
      :height="ModalHeight.FULL"
      :size="ModalSize.SM"
      @submit="proceedWithoutPersonalization"
    >
      <template #header>
        <h2 class="text-xl font-bold text-center pt-8">
          {{ $t('print.personalization-warning') }}
        </h2>
      </template>

      <template #body>
        <div class="mt-4 text-center space-y-4 p-3">
          <p class="text-sm text-on-surface-variant">
            {{ $t('print.personalization-warning-message') }}
          </p>
          <p class="text-sm text-on-surface-variant">
            {{ $t('print.personalization-warning-proceed-message') }}
          </p>
        </div>
      </template>

      <template #actions="{ closeModal: closeWarningModal }">
        <div class="py-6 gap-4 flex justify-center">
          <Btn
            :size="BtnSize.LG"
            :color="BtnColor.SECONDARY"
            @click="closeWarningModal"
          >
            {{ $t('common.cancel') }}
          </Btn>
          <Btn
            type="submit"
            :color="BtnColor.PRIMARY"
            :variant="BtnVariant.FILLED"
            :size="BtnSize.LG"
          >
            {{ $t('print.personalization-warning-proceed') }}
          </Btn>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Modal, ModalSize } from '~/components/modals/modal';
import { Btn, BtnColor, BtnSize, BtnVariant } from '~/components/btn';
import { Icon } from '~/components/icon';
import { Chip, ChipColor, ChipSize, ChipVariant } from '~/components/chip';
import { SelectInput } from '~/components/inputs/select-input';
import FilePrintPreview from '~/components/file-print-preview/file-print-preview.vue';
import { SelectLogoModal } from '~/pages/my-posts/_components/select-logo-modal';
import SparkleIcon from '~/assets/icons/sparkle.svg?component';
import XMarkSvg from '~/assets/icons/xmark.svg?component';
import imageProxyBuilder, { ResizeType } from '@bitpatty/imgproxy-url-builder';
import { useRuntimeConfig } from '#imports';
import { usePrintStore } from '~/store/print';
import { useLoader } from '~/composables/use-loader';
import { isRequired } from '~/utils/validators';
import { notify } from '@kyvg/vue3-notification';
import { NotificationType } from '~/components/notification';
import { type PriceTier, apiGenerateCustomPdf } from '~/api/print';
import { ModalHeight } from '../../../../../../components/modals/modal/modal.constants';
import { Tooltip } from '~/components/tool-tip';
import TooltipSvg from '~/assets/icons/tooltip.svg?component';
import { convertCanvasToPercentages } from '~/utils/print-customization';

const config = useRuntimeConfig();
const printStore = usePrintStore();
const { t } = useI18n();

const props = defineProps<{
  content: {
    id: number;
    title: string;
    description: string;
    mediumDescription: string;
    dimensions: string;
    imageUrl: string | null;
    previewUrl: string | null;
    customPreviewUrl: string | null;
    fileUrl: string | null;
    printProductId: number;
    isCustomizable?: boolean;
    productType: 'PRINT' | 'PRODUCT';
    quadrants?: Array<{
      id: number;
      x: number;
      y: number;
      width: number;
      height: number;
      page: number;
    }>;
    printProduct: {
      contentFilePrintId: number;
      sku?: string | null;
      priceTiers: PriceTier[];
      priceOverride?: number | null;
      availableQuantity?: number | null;
      hasInventoryLimit?: boolean;
    };
  };
}>();

type CustomizationData = {
  logos: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
    quadrantId: number;
  }>;
  currentPage: number;
  logoId: string;
  isPersonalized: boolean;
};

const emit = defineEmits<{
  (
    e: 'add-to-cart',
    data: {
      quantity: number;
      isCustomizable: boolean;
      customizationData?: CustomizationData | null;
      customPdfUrl?: string;
      contentId?: number;
    },
  ): void;
}>();

const isModalOpen = ref(false);
const selectedQuantity = ref<number | null>(null);
const selectedLogoId = ref<string>('');
const showPersonalizationWarning = ref(false);
const isGeneratingPdf = ref(false);
const filePrintPreviewRef = ref<InstanceType<typeof FilePrintPreview> | null>(
  null,
);

const currentPersonalizationState = ref<{
  isPersonalized: boolean;
  customization: CustomizationData | null;
}>({ isPersonalized: false, customization: null });

const currentCustomizationReady = ref<CustomizationData | null>(null);
const currentCustomizationData = ref<CustomizationData | null>(null);

const handlePersonalizationChanged = (data: {
  isPersonalized: boolean;
  customization?: any;
}) => {
  currentPersonalizationState.value = {
    isPersonalized: data.isPersonalized,
    customization: data.customization || null,
  };
};

const handleCustomizationReady = (customization: any) => {
  currentCustomizationReady.value = customization;
  currentCustomizationData.value = customization;
};

const openModal = () => {
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  showPersonalizationWarning.value = false;
  isGeneratingPdf.value = false;

  selectedQuantity.value = null;
  currentCustomizationData.value = null;
};

defineExpose({ openModal, closeModal });

const sortedPriceTiers = computed(() => {
  return [...(props.content.printProduct.priceTiers || [])].sort(
    (a, b) => a.quantity - b.quantity,
  );
});

const inventoryInfo = computed(() => {
  const { printProduct } = props.content;
  return {
    hasInventoryLimit: printProduct.hasInventoryLimit || false,
    availableQuantity: printProduct.availableQuantity,
    totalAllocated: printProduct.availableQuantity || 0,
  };
});

const isOutOfStock = computed(() => {
  return (
    sortedPriceTiers.value.length === 0 && inventoryInfo.value.hasInventoryLimit
  );
});

const quantityOptions = computed(() => {
  return sortedPriceTiers.value.map((tier) => {
    const effectivePrice =
      props.content.printProduct.priceOverride !== null &&
      props.content.printProduct.priceOverride !== undefined
        ? props.content.printProduct.priceOverride
        : tier.pricePerUnit;
    return {
      ...tier,
      pricePerUnit: effectivePrice,
      displayText: `${tier.quantity} (${printStore.currencySymbol}${(tier.quantity * effectivePrice).toFixed(2)})`,
    };
  });
});

const optimizedImageUrl = computed(() => {
  const imageSource =
    props.content.customPreviewUrl ||
    props.content.previewUrl ||
    props.content.imageUrl ||
    '';

  return imageProxyBuilder()
    .resize({ type: ResizeType.FILL_DOWN, width: 1200, height: 800 })
    .format('webp')
    .build({
      baseUrl: config.public.imgProxyBaseUrl || '',
      path: imageSource,
      addExtension: true,
    });
});

const isPdfFile = computed(() => {
  const fileUrl =
    props.content.fileUrl ||
    props.content.previewUrl ||
    props.content.imageUrl ||
    '';
  const isPdf =
    fileUrl.toLowerCase().includes('.pdf') ||
    props.content.mediumDescription.toLowerCase().includes('pdf');
  return isPdf;
});

const pdfFileUrl = computed(() => {
  return (
    props.content.fileUrl ||
    props.content.previewUrl ||
    props.content.imageUrl ||
    ''
  );
});

const handleCustomizationUpdate = (customization: {
  action: string;
  data?: CustomizationData;
}) => {
  if (customization.action === 'auto-save') {
    currentCustomizationData.value = customization.data || null;
  } else if (customization.action === 'reset') {
    currentCustomizationData.value = null;
  }
};

const addToCartWithCustomization = async (
  customizationData: CustomizationData | null = null,
) => {
  if (!selectedQuantity.value) return;

  const itemKey = props.content.printProduct.contentFilePrintId.toString();

  let finalPreviewUrl =
    props.content.previewUrl ||
    props.content.customPreviewUrl ||
    props.content.imageUrl;
  let finalContentId = props.content.id;
  let finalFileUrl = props.content.fileUrl || props.content.customPreviewUrl;

  let isPersonalizedFlag = false;

  if (customizationData && currentPersonalizationState.value.isPersonalized) {
    isGeneratingPdf.value = true;
    isPersonalizedFlag = true;

    try {
      const currentCustomization =
        currentCustomizationReady.value ||
        currentPersonalizationState.value.customization;

      if (!currentCustomization) {
        throw new Error('No customization data available');
      }

      const originalDimensions =
        filePrintPreviewRef.value?.getOriginalDimensions();
      const canvasDisplayDimensions =
        filePrintPreviewRef.value?.getCanvasDisplayDimensions();

      if (!originalDimensions || !canvasDisplayDimensions) {
        throw new Error('Dimensions not available');
      }

      const scaleX = originalDimensions.width / canvasDisplayDimensions.width;
      const scaleY = originalDimensions.height / canvasDisplayDimensions.height;

      const logosBeforeConversion = currentCustomization.logos || [];

      const customizationDataWithPercentages = {
        ...currentCustomization,
        logos: logosBeforeConversion.map((logo: any) => {
          const scaledLogo = {
            ...logo,
            x: logo.x * scaleX,
            y: logo.y * scaleY,
            width: logo.width * scaleX,
            height: logo.height * scaleY,
          };
          return convertCanvasToPercentages(scaledLogo, originalDimensions);
        }),
      };

      const payload = {
        contentId: props.content.id,
        fileId: props.content.id,
        originalPdfUrl: props.content.fileUrl || props.content.previewUrl || '',
        customizationData: customizationDataWithPercentages,
      };

      const response = await apiGenerateCustomPdf(payload);

      if (response.success && response.customPdfUrl && response.contentId) {
        finalPreviewUrl =
          response.customPreviewUrl ||
          props.content.previewUrl ||
          props.content.customPreviewUrl ||
          props.content.imageUrl;
        finalContentId = response.contentId;
        finalFileUrl = response.customPdfUrl;
      }
    } finally {
      isGeneratingPdf.value = false;
    }
  }

  printStore.addItem(
    itemKey,
    {
      id: finalContentId,
      title: props.content.title,
      description: props.content.description,
      contentTitle: props.content.title,
      imageUrl: props.content.imageUrl,
      previewUrl: finalPreviewUrl,
      fileUrl: finalFileUrl,
      customPreviewUrl: props.content.customPreviewUrl,
      productType: props.content.productType,
      printProduct: {
        contentFilePrintId: props.content.printProduct.contentFilePrintId,
        id: props.content.printProductId,
        description: props.content.mediumDescription,
        dimensions: props.content.dimensions,
        basePrice:
          sortedPriceTiers.value.find(
            (tier) => tier.quantity === selectedQuantity.value,
          )?.pricePerUnit || 0,
        priceOverride: props.content.printProduct.priceOverride ?? null,
        priceTiers: props.content.printProduct.priceTiers || [],
        fees: (props.content.printProduct as any).fees || [],
        weightOz: (props.content.printProduct as any).weightOz,
        productType: props.content.printProduct.productType,
        requiresPayment: props.content.printProduct.requiresPayment || false,
      },
      isCustomizable: props.content.isCustomizable || false,
      isPersonalized: isPersonalizedFlag,
      logoId: selectedLogoId.value || null,
    },
    selectedQuantity.value,
  );

  emit('add-to-cart', {
    quantity: selectedQuantity.value,
    isCustomizable: props.content.isCustomizable || false,
    customPdfUrl: finalPreviewUrl || undefined,
    contentId: finalContentId,
  });

  closeModal();
};

const handleAddToCart = async () => {
  if (!selectedQuantity.value) return;

  const itemKey = props.content.printProduct.contentFilePrintId.toString();

  if (props.content.isCustomizable) {
    const { isPersonalized } = currentPersonalizationState.value;

    if (isPersonalized) {
      const customizationData = currentPersonalizationState.value.customization;

      await addToCartWithCustomization(customizationData);
    } else {
      showPersonalizationWarning.value = true;
    }
  } else {
    await addToCartWithCustomization(null);
  }
};

const proceedWithoutPersonalization = async () => {
  await addToCartWithCustomization(null);
};
</script>
